import React, { Fragment, useEffect, } from 'react';
import { Button, } from 'reactstrap';
import { sum, sortBy, groupBy, trim, isEmpty, chunk, mapValues, orderBy, get, pick, omit, uniqBy, keyBy, } from 'lodash';
import { toast, } from 'react-toastify';
import { format as formatDate, addMonths, startOfDay, endOfDay, startOfMinute, endOfMinute, } from 'date-fns';
import { Link } from 'react-router-dom';
import numeral from 'numeral';
import moji from 'moji';
import jsbarcode from 'jsbarcode';
import { jsPDF } from 'jspdf';
import qs from 'qs';
import { useToggle, useCounter } from 'react-use';

import env from '../../env';
import firebase from '../../firebase';
import { getCollectionData, batch } from '../../shared/firebase';
import { computeCodFee, computeDocumentNumberForBarcode, computeCsvYamatoBoxSize, computeCsvSagawaBoxSize, types, returnDestinations, fields, statuses, boxSizes, deliveryTypes, deliveryType, formatPlanOfShippingRequest, } from '../../shared/models/shippingRequest';
import { amazonShippingRequestDocumentNumbersSettingFields, } from '../../shared/models/setting';
import { fieldDisplayValue, postalCodeWithHyphen, idWithTenant, } from '../../shared/util';
import { deliveryTimes, prefectures, } from '../../shared/config';
import { newId, fullPathWithParams, } from '../../util';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useCollectionFetch from '../hooks/useCollectionFetch';
import useCollectionsFetch from '../hooks/useCollectionsFetch';
import useDocumentsFetch from '../hooks/useDocumentsFetch';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useQueryParams from '../hooks/useQueryParams';
import AdminPage from '../hocs/AdminPage';
import ModelFormModal from '../modals/ModelFormModal';
import ShippingRequestFormModal from '../modals/ShippingRequestFormModal';
import ShippingRequestTypeBadge from '../ShippingRequestTypeBadge';
import ShippingRequestCsvFlowModal from '../modals/ShippingRequestCsvFlowModal';
import AddButton from '../AddButton';
import EditButton from '../EditButton';
import ExportButton from '../ExportButton';
import QueryInput from '../QueryInput';
import QueryBoolean from '../QueryBoolean';
import QuerySelector from '../QuerySelector';
import DateSelector from '../DateSelector';
import ModalButton from '../ModalButton';
import BatchImportButton from '../BatchImportButton';
import ProductTagsDisplay from '../ProductTagsDisplay';
import ShippingRequestStatusComputationDetailButton from '../ShippingRequestStatusComputationDetailButton';
import TenantTags from '../TenantTags';

const { keys, entries, values } = Object;
const db = firebase.firestore();
const storageRef = firebase.storage().ref();
const inventoriesRef = db.collection('inventories');
const locationsRef = db.collection('locations');
const tenantsRef = db.collection('tenants');
const tagsRef = db.collection('tags');
const productsRef = db.collectionGroup('products');
const sendersRef = db.collectionGroup('senders');
const shippingRequestsRef = db.collectionGroup('shippingRequests');
const typeOptions = entries(types).map(([k, v]) => ({ label: v.label, value: k }));
const statusesByLabel = mapValues(keyBy(entries(statuses), _ => _[1].label), '0');
const shippingRequestsCsvInfoRef = db.collection('settings').doc('shippingRequestsCsvInfo');
const amazonShippingRequestDocumentNumbersSettingRef = db.collection('settings').doc('amazonShippingRequestDocumentNumbers');
const statusOptions = entries(statuses).map(([k, v]) => ({ label: v.label, value: k }));
const replaceSpaceAndChar = _ => _.replace(/[ 　]([a-zA-Z])/g, 'ー$1');
const trimAndReplaceDashAndEtc = _ => trim(_).replace(/[-ー―−‐—]/g, '-').replace(/\t/, '');

export default AdminPage(function AdminShippingRequests (props) {
  const { location, history } = props;
  const queryParams = useQueryParams();
  const {
    createdAtStartOn: createdAtStartOnString,
    createdAtEndOn: createdAtEndOnString,
    startOn: startOnString,
    endOn: endOnString,
    shippingRequestIdQuery,
    statuses: statusesForFilter = [],
    tenantIds: tenantIdsForFilter = [],
    tagIds: tagIdsForFilter = [],
    documentNumberQuery,
    onlyShortPicked,
    limit = '1000',
  } = queryParams;
  const [version, { inc: incVersion }] = useCounter(0);
  const createdAtStartOn = new Date(createdAtStartOnString);
  const createdAtEndOn = new Date(createdAtEndOnString);
  const startOn = new Date(startOnString);
  const endOn = new Date(endOnString);
  const [tenantIdForFilter] = tenantIdsForFilter;
  const [type] = queryParams.types || [];
  let filteredShippingRequestsRef = shippingRequestsRef;
  if(!isEmpty(documentNumberQuery)) {
    filteredShippingRequestsRef = filteredShippingRequestsRef.where('documentNumbers', 'array-contains', documentNumberQuery).orderBy('createdAt', 'desc');
  } else {
    if(!isEmpty(tenantIdForFilter)) {
      filteredShippingRequestsRef = tenantsRef.doc(tenantIdForFilter).collection('shippingRequests');
    }
    if(!isEmpty(createdAtStartOnString)) {
      filteredShippingRequestsRef = filteredShippingRequestsRef.where('createdAt', '>=', startOfMinute(createdAtStartOn));
    }
    if(!isEmpty(createdAtEndOnString)) {
      filteredShippingRequestsRef = filteredShippingRequestsRef.where('createdAt', '<=', endOfMinute(createdAtEndOn));
    }
    if(!isEmpty(startOnString)) {
      filteredShippingRequestsRef = filteredShippingRequestsRef.where('completedAt', '>=', startOfDay(startOn));
    }
    if(!isEmpty(endOnString)) {
      filteredShippingRequestsRef = filteredShippingRequestsRef.where('completedAt', '<=', endOfDay(endOn));
    }
    if(!isEmpty(type)) {
      filteredShippingRequestsRef = filteredShippingRequestsRef.where('type', '==', type)
    }
    if(!isEmpty(statusesForFilter)) {
      filteredShippingRequestsRef = filteredShippingRequestsRef.where('status', 'in', statusesForFilter)
    }
    if(!isEmpty(startOnString) || !isEmpty(endOnString)) {
      filteredShippingRequestsRef = filteredShippingRequestsRef.orderBy('completedAt', 'desc');
    } else {
      filteredShippingRequestsRef = filteredShippingRequestsRef.orderBy('createdAt', 'desc');
    }
  }
  const limitNumber = limit ? parseInt(limit, 10) : undefined;
  const hasClientSideFilter = (!isEmpty(shippingRequestIdQuery) && isEmpty(documentNumberQuery)) || !isEmpty(tagIdsForFilter) || onlyShortPicked === '1';
  if(limitNumber && !hasClientSideFilter) {
    filteredShippingRequestsRef = filteredShippingRequestsRef.limit(limitNumber);
  }
  const locations = useCollectionFetch(locationsRef);
  const locationsById = keyBy(locations, 'id');
  const generalSetting = useDocumentSubscription(db.collection('settings').doc('general'));
  const shippingRequestsCsvInfo = useDocumentSubscription(shippingRequestsCsvInfoRef);
  const amazonShippingRequestDocumentNumbersSetting = useDocumentSubscription(amazonShippingRequestDocumentNumbersSettingRef);
  const shippingRequests = useCollectionFetch(filteredShippingRequestsRef, [createdAtStartOnString, createdAtEndOnString, startOnString, endOnString, tenantIdForFilter, type, statusesForFilter.join(''), documentNumberQuery, limit, hasClientSideFilter, version]);
  const tenants = useCollectionFetch(tenantsRef.orderBy('createdAt'));
  const tenantsById = keyBy(tenants, 'id');
  const tenantOptions = tenants.map(_ => ({ label: `[${_.id}] ${_.name}`, value: _.id }));
  const tags = useCollectionFetch(tagsRef.orderBy('createdAt'));
  const tagsById = keyBy(tags, 'id');
  const tagOptions = tags.map(_ => ({ label: _.name, value: _.id }));
  const products = useDocumentsFetch(uniqBy(shippingRequests.map(sr => sr.items.map(_ => sr.ref.parent.parent.collection('products').doc(_.productId))).flat(), idWithTenant), [shippingRequests], { initialItems: null });
  const productsById = keyBy(products?.filter(_ => _), _ => idWithTenant(_.ref));
  const senders = useCollectionFetch(sendersRef);
  const sendersById = keyBy(senders, 'id');
  const isSearching = !isEmpty(documentNumberQuery);
  const shippingRequestItems = shippingRequests
    .map((shippingRequest) => {
      const tenant = tenantsById[shippingRequest.ref.parent.parent.id];
      const _deliveryType = deliveryType(generalSetting, shippingRequest, keyBy(products?.filter(_ => _.ref.parent.parent.id === shippingRequest.ref.parent.parent.id), 'id'), tenant?.allowsMultiNeko);
      return { ...shippingRequest, deliveryType: _deliveryType, };
    });
  let filteredShippingRequestItems = shippingRequestItems;
  if(shippingRequestIdQuery && isEmpty(documentNumberQuery)) {
    filteredShippingRequestItems = filteredShippingRequestItems.filter(_ => idWithTenant(_.ref).includes(shippingRequestIdQuery));
  }
  if(!isEmpty(tagIdsForFilter)) {
    filteredShippingRequestItems = filteredShippingRequestItems.filter(shippingRequest => {
      const tenant = tenantsById[shippingRequest.ref.parent.parent.id];
      return tagIdsForFilter.some(tagId => (tenant?.tagIds || []).includes(tagId));
    });
  }
  if(onlyShortPicked === '1') {
    filteredShippingRequestItems = filteredShippingRequestItems.filter((shippingRequest) => {
      const { type = 'normal', items = [] } = shippingRequest;
      return items.some(item => (shippingRequest[`inventoryIds-${item.productId}`] || []).length < (item[types[type].quantityName] || 0));
    });
  }
  const sortedShippingRequests = orderBy(filteredShippingRequestItems, _ => _.createdAt.toDate(), 'desc').slice(0, limitNumber);
  const hasNoItems = (deliveryType) => {
    return shippingRequestItems.filter(_ => _.status === 'waitingToShip' && _.deliveryType === deliveryType).length === 0;
  };
  const rowsByType = (targetDeliveryType) => {
    return sortedShippingRequests
      .filter(_ => _.status === 'waitingToShip')
      .filter(_ => _.deliveryType === targetDeliveryType)
  };
  const rowsByDeliveryType = async (targetDeliveryType) => {
    const isCashOnDeliveryAll = targetDeliveryType.includes('-cod-all');
    return sortBy((await chunk(rowsByType(targetDeliveryType), 50).reduce(async (x, rows) => {
      const prevs = await x;

      const data = await Promise.all(rows.map(async (shippingRequest) => {
        const { type = 'normal', id, items, deliveryType, senderId, orderNumber, postalCode, prefecture, city, address1, address2, name, phone, deliveryDate, deliveryTime = 'free', shippingOptionValues = [], status, ref: { parent: { parent: { id: tenantId } } }, codAmount, } = shippingRequest;
        const sender = sendersById[senderId];
        const tenant = tenantsById[tenantId];
        const itemNames = items.map(_ => productsById[`${tenant.id}-${_.productId}`].itemName);
        const product = productsById[items[0].productId];
        const allocatedId = (shippingRequest.allocatedInventoryIds || []).find(_ => _.productId === items[0].productId)?.inventoryId;
        const inventory = allocatedId ? await inventoriesRef.doc(allocatedId).get().then(_ => _.exists ? { id: _.id, ..._.data() } : null) : null;
        const location = locationsById[inventory?.locationId];

        return targetDeliveryType.match('fukuyama') ? {
            荷受人コード: '',
            電話番号: replaceSpaceAndChar(trimAndReplaceDashAndEtc(phone || '1234567890')),
            住所: prefectures[prefecture] + city,
            住所２: replaceSpaceAndChar(trimAndReplaceDashAndEtc(address1 + (address2 || ''))).slice(0, 20),
            住所３: replaceSpaceAndChar(trimAndReplaceDashAndEtc(address1 + (address2 || ''))).slice(20, 40),
            名前: replaceSpaceAndChar(trimAndReplaceDashAndEtc(name)).slice(0, 20),
            名前２: replaceSpaceAndChar(trimAndReplaceDashAndEtc(name)).slice(20, 40),
            郵便番号: postalCodeWithHyphen(postalCode),
            特殊計: '',
            着店コード: '',
            荷送人コード: '001',
            荷送担当者: '',
            個数: 1,
            才数: '',
            重量: '',
            輸送商品１: deliveryTime !== 'free' && deliveryTime ? '100' : '',
            輸送商品２: deliveryTime !== 'free' && deliveryTime && deliveryTimes[deliveryTime].forFukuyamaShippingCsv || '',
            品名記事１: itemNames.slice(0, 1).join('.'),
            品名記事２: items.map(_ => _.quantity).join('.'),
            品名記事３: '',
            品名記事４: '',
            品名記事５: '',
            品名記事６: '',
            配達指定日: deliveryDate && formatDate(deliveryDate.toDate(), 'yyyyMMdd'),
            必着区分: '',
            お客様管理番号: `${tenant.id}-${id}`,
            元払区分: 1,
            保険金額: '',
            出荷日付: formatDate(new Date(), 'yyyyMMdd'),
            登録日付: '',
          } : {
          ...(
            (deliveryType === 'neko' || deliveryType.match(/^yamato/)) ?
              { 送り状種類: ({ neko: 'A', 'yamato-cod': 5, yamato: 0 })[deliveryType], }
            : { 元着区分: ({ 'sagawa-cod-all': 2, 'sagawa-cod': 2, sagawa: 1, 'nihon-cod': 1, nihon: 0, 'fukuyama-cod': 1, fukuyama: 1, 'seino-cod': 1, seino: 1, })[deliveryType], }
          ),
          ...(
            isCashOnDeliveryAll && {
              代引金額: sum([codAmount, computeCodFee(tenant.codFees, codAmount)]),
              お客様コード: tenant.codCustomerCode,
            }
          ),
          会社: tenant.name,
          出荷依頼ID: `${tenant.id}-${id}`,
          商品ID: items.map(_ => `${tenant.id}-${_.productId}`).join(','),
          品名: itemNames.slice(0, 1).map(_ => targetDeliveryType.startsWith('sagawa') ? moji(_).convert('HE', 'ZE').convert('HS', 'ZS').toString() : _).join(','),
          出荷数量: items.map(_ => _[types[type].quantityName]).join(','),
          配送先郵便番号: postalCodeWithHyphen(postalCode),
          配送先都道府県: prefectures[prefecture],
          配送先市区郡町村: city,
          ['配送先住所1']: replaceSpaceAndChar(trimAndReplaceDashAndEtc(address1 + (address2 || ''))).slice(0, 16),
          ['配送先住所2']: replaceSpaceAndChar(trimAndReplaceDashAndEtc(address1 + (address2 || ''))).slice(16),
          配送先宛名: replaceSpaceAndChar(trimAndReplaceDashAndEtc(name)),
          配送先電話番号: replaceSpaceAndChar(trimAndReplaceDashAndEtc(phone || '1234567890')),
          配送日: (
            deliveryType === 'neko' ? ''
            : deliveryType.match(/^sagawa/) ? (deliveryDate && formatDate(deliveryDate.toDate(), 'yyyyMMdd'))
            : (deliveryDate && formatDate(deliveryDate.toDate(), 'yyyy/MM/dd'))
          ),
          配送時間: deliveryType === 'neko' ? '' : deliveryTimes[deliveryTime || 'free'].forShippingCsv,
          注文番号: trimAndReplaceDashAndEtc(orderNumber),
          配送元郵便番号: postalCodeWithHyphen(sender.postalCode),
          配送元都道府県: prefectures[sender.prefecture],
          配送元市区郡町村: sender.city,
          ['配送元町・番地']: sender.address1,
          ['配送元マンション・ビル名']: sender.address2,
          配送元名: sender.name,
          配送元電話番号: sender.phone,
          locationKey: location?.sortKey
        };
      }));
      return [...prevs, ...data];
    }, Promise.resolve([]))), 'locationKey');
  };
  const rowsToPick = async () => {
    const rows = await Promise.all(
      sortedShippingRequests
        .flatMap(shippingRequest => (shippingRequest.items || []).map(_ => ({ shippingRequest, item: _ })))
        .map(async ({ shippingRequest, item }) => {
          const { ref, } = shippingRequest;
          const tenantId = ref.parent.parent.id;
          const { productId, goodQuantity: quantity, } = item;
          const product = productsById[[tenantId, productId].join('-')];
          const allocatedIds = (shippingRequest.allocatedInventoryIds || []).filter(_ => _.productId === productId).map(_ => _.inventoryId);
          const inventories = allocatedIds.length > 0
            ? await Promise.all(allocatedIds.map(_ => inventoriesRef.doc(_).get())).then(_ => _.filter(_ => _.exists).map(_ => ({ id: _.id, ref: _.ref, ..._.data() })))
            : [];

          const inventoriesGroupedByLocationId = groupBy(inventories, 'locationId');

          return entries(inventoriesGroupedByLocationId).map(([locationId, locationInventories]) => {
            const location = locationsById[locationId];
            return {
              shippingRequestId: idWithTenant(ref),
              productId: idWithTenant(product?.ref),
              productName: product?.name,
              barcodeInfo: product?.barcodeInfo,
              fnsku: product?.fnsku,
              quantity: locationInventories.length,
              locationKey: location?.sortKey,
            };
          });
        })
    );
    return rows.flat();
  };
  const rowsForDelivery = async (isAll) => {
    const shippingRequests = isAll ? await getCollectionData(shippingRequestsRef.orderBy('createdAt', 'desc')) : sortedShippingRequests;
    return shippingRequests.flatMap(shippingRequest => (shippingRequest.shippingItems || []).map(_ => ({ shippingRequest, shippingItem: _ }))).map(({ shippingRequest, shippingItem }) => {
      const { ref, type = 'normal', status, createdAt, completedAt, shippingItems, postalCode, prefecture, city, address1, address2, name, amazonLabelsCount, plan, processStartedAt, } = formatPlanOfShippingRequest(shippingRequest);
      const { documentNumber, boxSize, } = shippingItem;
      const shippingItemsGroupedBySize = groupBy(shippingItems, 'boxSize');
      return {
        id: idWithTenant(ref),
        type,
        status,
        processStartedAt: processStartedAt && formatDate(processStartedAt.toDate(), 'yyyy/MM/dd'),
        boxSize,
        documentNumber,
        documentNumber12: type === 'amazon' ? computeDocumentNumberForBarcode(shippingRequest) : '',
        amazonLabelsCount,
        amazonFcId: plan?.DestinationFulfillmentCenterId,
        postalCode,
        prefecture: prefectures[prefecture],
        city,
        address1,
        address2,
        name,
        createdAt: formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss'),
        completedAt: completedAt && formatDate(completedAt.toDate(), 'yyyy/MM/dd'),
      };
    });
  };
  const onDownloaded = async (deliveryType, rows) => {
    const ids = rows.map((row) => {
      const [, tenantId, shippingRequestId] = (row['出荷依頼ID'] || row['お客様管理番号']).match(new RegExp(`^(${env('TENANT_PREFIX')}\\d+)-(.+)`));
      return { tenantId, shippingRequestId };
    });
    await shippingRequestsCsvInfoRef.set({
      deliveryType,
      downloadedAt: new Date(),
      status: 'downloaded',
      ids,
    }, { merge: true });
    await batch(db, ids, (batch, { tenantId, shippingRequestId }) => {
      batch.update(tenantsRef.doc(tenantId).collection('shippingRequests').doc(shippingRequestId), { status: 'processing' });
    });
    incVersion();
  };
  const processRows = (rows) => {
    return uniqBy(rows.slice().reverse(), _ => _['お客様管理ナンバー'] || _['お客様管理番号'] || _['顧客管理番号']);
  };
  const processRow = (batch, row) => {
    const _id = row['お客様管理ナンバー'] || row['お客様管理番号'] || row['顧客管理番号'] || row['お客様側管理番号'];
    const _documentNumber = row[keys(row).find(_ => _.match(/お問い?合せ送り状/) || _.match('お問合せNO') || _.match('お問い合わせ番号') || _.match('送り状番号') || _ === '伝票番号')];
    if(!_id || !_documentNumber) return;

    const documentNumber = _documentNumber.replace(/[ 　]/g, '');
    const id = _id.replace(/[ 　]/g, '');
    const [, tenantId, shippingRequestId] = id.match(new RegExp(`^(${env('TENANT_PREFIX')}\\d+)-(S.+)`)) || [];
    if(!tenantId || !shippingRequestId) return;

    const type = row['サイズ'] != null ? 'yamato' : 'sagawa';
    const shippingItems = ({
      yamato: _ => {
        const boxSize = computeCsvYamatoBoxSize(row);
        return [{ documentNumber, boxSize }];
      },
      sagawa: _ => {
        const boxSizes = computeCsvSagawaBoxSize(row);
        return boxSizes.length > 0 ? boxSizes.map(_ => ({ documentNumber, boxSize: _ })) : [{ documentNumber, boxSize: null }];
      },
    })[type]();
    const ref = tenantsRef.doc(tenantId).collection('shippingRequests').doc(shippingRequestId);
    documentNumber && batch.update(ref, { documentNumbers: [documentNumber], shippingItems, });
  };
  const processStatusRows = async (rows) => {
    const parsedRows = await Promise.all(rows.map(async (row) => {
      const id = trim(row.id);
      const _status = trim(row.status);
      const status = statuses[_status] ? _status : statusesByLabel[_status];
      const [, tenantId, shippingRequestId] = id.match(new RegExp(`^(${env('TENANT_PREFIX')}\\d+)-(S.+)$`)) || [];
      const ref = tenantId && tenantsRef.doc(tenantId).collection('shippingRequests').doc(shippingRequestId);
      const doc = ref && await ref.get();
      const error = !doc?.exists ? '出荷依頼が見つかりません' : !status ? 'ステータスが不正です' : null;
      return { id, ref, status, currentStatus: doc?.data()?.status, error };
    }));
    const errorRows = parsedRows.filter(_ => _.error);
    if(errorRows.length > 0) throw new Error(errorRows.map(_ => `${_.id || '(空)'}: ${_.error}`).join('\n'));

    return uniqBy(parsedRows.slice().reverse(), 'id');
  };
  const processStatusRow = (batch, { ref, status, currentStatus }) => {
    batch.update(ref, {
      status,
      ...(!['shipped', 'discarded'].includes(currentStatus) && ['shipped', 'discarded'].includes(status) && { completedAt: new Date() }),
    });
  };
  useEffect(() => {
    if(isEmpty(statusesForFilter)) {
      const path = fullPathWithParams({ statuses: ['waitingToShip'], }, location);
      history.replace(encodeURI(path));
    }
  }, []);
  useEffect(() => {
    if(amazonShippingRequestDocumentNumbersSetting === null) {
      amazonShippingRequestDocumentNumbersSettingRef.set({ updatedAt: new Date(), }, { merge: true });
    }
  }, [amazonShippingRequestDocumentNumbersSetting]);

  return (
    <div className="admin-shippingRequests container-fluid">
      <div className="p-4 bg-white my-4">
        <div className="d-flex justify-content-center mb-3">
          <h4>出荷依頼管理</h4>
        </div>
        <div className="mb-3 d-flex align-items-end justify-content-between gap-1">
          <div className="d-flex align-items-end gap-1">
            <div className="card" style={{ opacity: isSearching && 0.4 }}>
              <div className="card-header">
                絞り込み
              </div>
              <div className="card-body">
                <div className="d-flex align-items-center flex-wrap gap-1">
                  <DateSelector paramName="createdAtStartOn" label="出荷依頼日時(開始)" disabled={isSearching || queryParams.startOn || queryParams.endOn} dateFormat="yyyy-MM-dd HH:mm" pickerProps={{ showTimeInput: true, timeInputLabel: 'Time:', }} />
                  <DateSelector paramName="createdAtEndOn" label="出荷依頼日時(終了)" disabled={isSearching || queryParams.startOn || queryParams.endOn} invalid={createdAtStartOn > createdAtEndOn} dateFormat="yyyy-MM-dd HH:mm" pickerProps={{ showTimeInput: true, timeInputLabel: 'Time:', }} />
                </div>
                <div className="d-flex align-items-center flex-wrap gap-1">
                  <DateSelector paramName="startOn" label="出荷日(開始)" disabled={isSearching || queryParams.createdAtStartOn || queryParams.createdAtEndOn} />
                  <DateSelector paramName="endOn" label="出荷日(終了)" disabled={isSearching || queryParams.createdAtStartOn || queryParams.createdAtEndOn} invalid={startOn > endOn} />
                </div>
                <div className="d-flex flex-wrap gap-1">
                  <div style={{ width: 300 }} >
                    <QuerySelector className="w-100" paramName="types" options={typeOptions} label="種別で絞込み" disabled={isSearching} />
                  </div>
                  <div style={{ width: 300 }} >
                    <QuerySelector className="w-100" paramName="statuses" isMulti options={statusOptions} label="ステータスで絞込み" disabled={isSearching} />
                  </div>
                  <div style={{ width: 150 }}>
                    <QuerySelector className="w-100" paramName="tenantIds" options={tenantOptions} label="テナントで絞込み" disabled={isSearching} />
                  </div>
                  <div style={{ width: 150 }}>
                    <QuerySelector className="w-100" paramName="tagIds" isMulti options={tagOptions} label="タグで絞込み" disabled={isSearching} />
                  </div>
                  <div style={{ width: 150 }}>
                    <QueryInput width={null} paramName="shippingRequestIdQuery" label="出荷依頼IDで検索" disabled={isSearching} />
                  </div>
                </div>
                <div>
                  <QueryBoolean paramName="onlyShortPicked" label="ピッキング数が依頼数に達していないもののみ" defaultValue={'0'} />
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-header">
                検索
              </div>
              <div className="card-body">
                <div>
                  <QueryInput paramName="documentNumberQuery" label="伝票番号" />
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-header">
                その他
              </div>
              <div className="card-body">
                <div>
                  <QueryInput paramName="limit" label="表示件数" defaultValue="1000" />
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-1">
            <ExportButton fileName={`ピッキングリスト.csv`} label="ピッキングリスト" rows={rowsToPick.bind(null, false)} />
            <ExportButton fileName={`shippingRequests.csv`} label="出荷データ" rows={rowsForDelivery.bind(null, false)} />
            <ExportButton fileName={`allShippingRequests.csv`} label="全出荷データ" rows={rowsForDelivery.bind(null, true)} />
            <EditButton label="Amazon納品依頼伝票番号設定" itemRef={amazonShippingRequestDocumentNumbersSettingRef} FormModal={ModelFormModal} formProps={{ title: 'Amazon納品依頼伝票番号設定', fields: amazonShippingRequestDocumentNumbersSettingFields(), }} />
            <BatchImportButton encoding="UTF-8" label="伝票番号登録CSV (UTF-8)" processRows={processRows} processRow={processRow} onFailed={_ => toast.error(_.message)} onComplete={_ => incVersion()} />
            <BatchImportButton label="伝票番号登録CSV" processRows={processRows} processRow={processRow} onFailed={_ => toast.error(_.message)} onComplete={_ => incVersion()} />
            <ModalButton
              color="secondary"
              label="ステータス一括変更"
              content={_ => (
                <div>
                  <ol className="pl-3">
                    <li>現在の絞り込み結果をエクスポート</li>
                    <li>status列を変更後のステータスに書き換え</li>
                    <li>インポート</li>
                  </ol>
                  <div className="small text-muted mb-3">
                    ステータス: {values(statuses).map(_ => _.label).join(' / ')}
                  </div>
                  <div className="d-flex gap-1">
                    <ExportButton fileName="shippingRequestStatuses.csv" label={`エクスポート (${sortedShippingRequests.length}件)`} rows={_ => sortedShippingRequests.map(_ => ({ id: idWithTenant(_.ref), status: statuses[_.status].label }))} />
                    <BatchImportButton label="インポート" processRows={processStatusRows} processRow={processStatusRow} onFailed={_ => toast.error(_.message)} onComplete={_ => incVersion()} />
                  </div>
                </div>
              )}
            />
            {
              entries(omit(deliveryTypes, ['amazon'])).map(([k, { label, code }]) => {
                return (
                  <ExportButton fileName={`${code}.csv`} label={`${label}CSV (${rowsByType(k).length})`} rows={rowsByDeliveryType.bind(null, k)} disabled={hasNoItems(k) || products == null} onFinish={onDownloaded.bind(null, k)} />
                );
              })
            }
            <AmazonListButton amazonShippingRequestDocumentNumbersSetting={amazonShippingRequestDocumentNumbersSetting} shippingRequests={rowsByType('amazon')} disabled={hasNoItems('amazon')} />
          </div>
        </div>
        <div className="mb-2">
          {numeral(sortedShippingRequests.length).format('0,0')}件
        </div>
        <div className="overflow-scroll position-relative" style={{ zIndex: 0 }}>
          {
            sortedShippingRequests.length > 0 ? (
              <table className="table sticky-table table-bordered">
                <thead className="thead-light text-center">
                  <tr className="text-nowrap">
                    <th></th>
                    <th style={{ minWidth: 150 }}>種別</th>
                    <th style={{ minWidth: 100 }}>ステータス</th>
                    <th style={{ minWidth: 100 }}>作業開始日</th>
                    <th style={{ minWidth: 150 }}>配送方法</th>
                    <th style={{ minWidth: 150 }}>会社</th>
                    <th style={{ minWidth: 150 }}>
                      <div>出荷依頼ID</div>
                      <div>出荷依頼日時</div>
                      <div>出荷日</div>
                    </th>
                    <th style={{ minWidth: 150 }}>商品ID</th>
                    <th style={{ minWidth: 150 }}>品名</th>
                    <th style={{ minWidth: 200 }}>商品名</th>
                    <th style={{ minWidth: 150 }}>
                      SKU<br />
                      FNSKU<br />
                      ASIN<br />
                    </th>
                    <th style={{ minWidth: 100 }}>出荷数量</th>
                    <th style={{ minWidth: 100 }}>ピッキング済み数</th>
                    <th style={{ minWidth: 150 }}>配送先郵便番号</th>
                    <th style={{ minWidth: 120 }}>配送先都道府県</th>
                    <th style={{ minWidth: 150 }}>配送先市区郡町村</th>
                    <th style={{ minWidth: 150 }}>配送先住所1</th>
                    <th style={{ minWidth: 150 }}>配送先住所2</th>
                    <th style={{ minWidth: 150 }}>配送先宛名</th>
                    <th style={{ minWidth: 150 }}>配送先電話番号</th>
                    <th style={{ minWidth: 150 }}>配送日</th>
                    <th style={{ minWidth: 150 }}>配送時間</th>
                    <th style={{ minWidth: 150 }}>オプション</th>
                    <th style={{ minWidth: 150 }}>注文番号</th>
                    <th style={{ minWidth: 150 }}>配送元郵便番号</th>
                    <th style={{ minWidth: 120 }}>配送元都道府県</th>
                    <th style={{ minWidth: 150 }}>配送元市区郡町村</th>
                    <th style={{ minWidth: 150 }}>配送元町・番地</th>
                    <th style={{ minWidth: 150 }}>配送元マンション・ビル名</th>
                    <th style={{ minWidth: 150 }}>配送元名</th>
                    <th style={{ minWidth: 150 }}>配送元電話番号</th>
                    <th style={{ minWidth: 200 }}>添付ファイル</th>
                    <th style={{ minWidth: 150 }}>伝票番号/箱サイズ/出荷日</th>
                    <th style={{ minWidth: 150 }}>返送先</th>
                    <th style={{ minWidth: 150 }}>管理者メモ</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    sortedShippingRequests.map((shippingRequest) => {
                      const { id, ref, type = 'normal', shippingItems = [], items = [], senderId, orderNumber, postalCode, prefecture, city, address1, address2, name, phone, deliveryDate, deliveryTime = 'free', shippingOptionValues = [], status, adminNote, ref: { parent: { parent: { id: tenantId } } }, files = [], returnDestination, plan, createdAt, completedAt, processStartedAt, isHiddenDeliveryType = false, shopifySource, rakutenSource, yahooSource, ttsSource, mercariSource, qoo10Source, } = shippingRequest;
                      const sender = sendersById[senderId];
                      const rowItems = items.length > 0 ? items : [{}];
                      const tenant = tenantsById[tenantId];
                      const tenantProducts = (products || []).filter(_ => _.ref.parent.parent.id === tenantId);
                      const { color } = statuses[status];
                      const _deliveryType = deliveryType(generalSetting, shippingRequest, keyBy(tenantProducts, 'id'), tenant?.allowsMultiNeko);
                      const processValues = (values) => {
                        return {
                          ...values,
                          documentNumbers: values.shippingItems.map(_ => _.documentNumber).filter(_ => _),
                          ...(!['shipped', 'discarded'].includes(status) && ['shipped', 'discarded'].includes(values.status) && { completedAt: new Date() }),
                        };
                      };
                      const onEdited = async (values) => {
                        if(shippingRequest.returnDestination === 'logisp' && shippingRequest.status === 'cancelling' && values.status === 'cancelled') {
                          const { docs: [lastReceivingRequestDoc] } = await tenant.ref.collection('receivingRequests').orderBy('createdAt', 'desc').limit(1).get();
                          await tenant.ref.collection('receivingRequests').doc('W' + newId(lastReceivingRequestDoc)).set({
                            documentNumber: '',
                            items: items.map(_ => ({ productId: _.productId, quantity: _[types[type].quantityName], goodQuantity: 0, ngQuantity: 0 })),
                            productIds: items.map(_ => _.productId),
                            pendingItems: [],
                            status: 'initial',
                            viaCancel: true,
                            shippingRequestId: shippingRequest.id,
                            createdAt: new Date(),
                          });
                        }
                        incVersion();
                      };

                      return tenant != null && (
                        <Fragment key={idWithTenant(ref)}>
                          {
                            rowItems.map((item, i) => {
                              const { productId, ngQuantity } = item;
                              const product = productsById[[tenant.id, productId].join('-')];
                              return (
                                <tr key={tenant.id + id + i} className={`table-${color}`}>
                                  {
                                    i === 0 && (
                                      <Fragment>
                                        <td rowSpan={rowItems.length} className="text-nowrap">
                                          <EditButton itemRef={ref} onFinish={onEdited} processValues={processValues} FormModal={ShippingRequestFormModal} formProps={{ products: tenantProducts, tenant, admin: true }} />
                                          {
                                            type === 'discard' && (
                                              <ModalButton
                                                label="廃棄"
                                                title="廃棄"
                                                className="ml-2"
                                                content={_ => <InventoriesModalContent shippingRequest={shippingRequest} tenant={tenant} locationsById={locationsById} />}
                                              />
                                            )
                                          }
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          <ShippingRequestTypeBadge type={type} />
                                          {shopifySource != null && <div><span className="badge badge-info">Shopify</span></div>}
                                          {rakutenSource != null && <div><span className="badge badge-info">楽天</span></div>}
                                          {yahooSource != null && <div><span className="badge badge-info">Yahoo</span></div>}
                                          {ttsSource != null && <div><span className="badge badge-info">TikTok Shop</span></div>}
                                          {qoo10Source != null && <div><span className="badge badge-info">Qoo10</span></div>}
                                          {mercariSource != null && <div><span className="badge badge-info">メルカリ</span></div>}
                                          {
                                            plan && (
                                              <div>{plan.ShipmentId}</div>
                                            )
                                          }
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {statuses[status].label}
                                          <ShippingRequestStatusComputationDetailButton shippingRequest={shippingRequest} />
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {processStartedAt && formatDate(processStartedAt.toDate(), 'yyyy/MM/dd')}
                                        </td>
                                        <td rowSpan={rowItems.length}>{!isHiddenDeliveryType && deliveryTypes[_deliveryType].label}</td>
                                        <td rowSpan={rowItems.length}>
                                          <Link to={`/admin/tenants/${tenant.id}`}>
                                            {tenant.name}
                                          </Link>
                                          <TenantTags tagIds={tenant.tagIds} tagsById={tagsById} />
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          <div>{tenant.id}-{id}</div>
                                          <div>{formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm')}</div>
                                          <div>{completedAt ? formatDate(completedAt.toDate(), 'yyyy/MM/dd') : '(出荷日なし)'}</div>
                                        </td>
                                      </Fragment>
                                    )
                                  }
                                  <td>{product && `${tenant.id}-${product.id}`}</td>
                                  <td>{product && product.itemName}</td>
                                  <td>
                                    <ProductTagsDisplay product={product} productTags={[]} />
                                    <div>{product && product.name}</div>
                                  </td>
                                  <td>
                                    {product?.sku}<br />
                                    {product?.fnsku}<br />
                                    {product?.asin}
                                  </td>
                                  <td className="text-right">{item[types[type].quantityName]}</td>
                                  <td className="text-right">{(shippingRequest[`inventoryIds-${productId}`] || []).length}</td>
                                  {
                                    i === 0 && (
                                      <Fragment>
                                        <td rowSpan={rowItems.length}>
                                          {postalCode}
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {prefectures[prefecture]}
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {city}
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {address1}
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {address2}
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {name}
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {phone}
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {fieldDisplayValue(deliveryDate, fields().deliveryDate)}
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {fieldDisplayValue(deliveryTime, fields().deliveryTime)}
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {fieldDisplayValue(shippingOptionValues, fields({ type, }).shippingOptionValues)}
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {orderNumber}
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {sender && sender.postalCode}
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {sender && prefectures[sender.prefecture]}
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {sender && sender.city}
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {sender && sender.address1}
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {sender && sender.address2}
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {sender && sender.name}
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {sender && sender.phone}
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {
                                            files.map((file) => {
                                              return (
                                                <div className="mb-1">
                                                  {file.name}
                                                  <a className="ml-2" href={file.downloadUrl} target="_blank">
                                                    <span className="fas fa-eye" />
                                                  </a>
                                                </div>
                                              );
                                            })
                                          }
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {
                                            shippingItems.map((shippingItem, i) => {
                                              const { documentNumber, boxSize, shippedOn, } = shippingItem;
                                              return (
                                                <div key={i}>
                                                  {documentNumber} {type === 'amazon' ? `(${computeDocumentNumberForBarcode(shippingRequest)}) ` : ''} / {boxSize && boxSizes[boxSize]?.label} / {shippedOn && formatDate(shippedOn.toDate(), 'yyyy/MM/dd')}
                                                </div>
                                              );
                                            })
                                          }
                                        </td>
                                        <td rowSpan={rowItems.length} style={{ whiteSpace: 'pre-line' }}>
                                          {get(returnDestinations, [returnDestination, 'label'])}
                                        </td>
                                        <td rowSpan={rowItems.length} style={{ whiteSpace: 'pre-line' }}>
                                          {adminNote}
                                        </td>
                                      </Fragment>
                                    )
                                  }
                                </tr>
                              )
                            })
                          }
                        </Fragment>
                      );
                    })
                  }
                </tbody>
              </table>
            ) : (
              <div>
                出荷依頼が見つかりません
              </div>
            )
          }
        </div>
      </div>
      {
        ['downloaded', 'processed', 'went', 'printed'].includes(get(shippingRequestsCsvInfo, 'status')) && (
          <ShippingRequestCsvFlowModal shippingRequestsCsvInfo={shippingRequestsCsvInfo} onCancel={_ => incVersion()}/>
        )
      }
    </div>
  );
});

function AmazonListButton (props) {
  const { amazonShippingRequestDocumentNumbersSetting, shippingRequests, ...extraProps } = props;
  const [isClicked, toggleClicked] = useToggle(false);
  const scale = 2;
  const pdfWidth = 210;
  const barcodeWidth = 29;
  const barcodeHeight = 20;
  const textAreaWidth = 80;
  const onClick = () => {
    toggleClicked(true);
    window.open(`/admin/amazonShippingRequestList?${qs.stringify({ ids: shippingRequests.map(_ => ({ tenantId: _.ref.parent.parent.id, shippingRequestId: _.id })), })}`);
  };
  const onSubmit = async () => {
    const { start, end, current } = amazonShippingRequestDocumentNumbersSetting;

    const newCurrent = current + shippingRequests.length > end ? start + (shippingRequests.length - 1 - (end - current)) : current + shippingRequests.length;
    await Promise.all(shippingRequests.map((shippingRequest, i) => {
      const documentNumber = current + (i + 1) > end ? start + (i - (end - current)) : current + (i + 1);
      return shippingRequest.ref.update({ status: 'processing', shippingItems: [{ documentNumber, boxSize: null, shippedOn: null }], });
    }));
    await amazonShippingRequestDocumentNumbersSettingRef.update({
      current: newCurrent,
    });
    toggleClicked(false);
  };

  return (
    <Fragment>
      <Button onClick={onClick} {...extraProps}>
        Amazon納品リスト
      </Button>
      {isClicked && <ModelFormModal isOpen title="確認" fields={{}} onClickClose={toggleClicked.bind(null, false)} onSubmit={onSubmit} submitLabel="印刷した" renderFormHeader={_ => <div>Amazon納品リストを印刷しましたか？（出荷作業中にします）</div>} />}
    </Fragment>
  );
}

function InventoriesModalContent (props) {
  const { tenant, shippingRequest, locationsById, } = props;
  const [version, { inc: updateVersion }] = useCounter(0);
  const inventories = useCollectionsFetch(shippingRequest.items.map(_ => inventoriesRef.where('tenantId', '==', tenant.id).where('productId', '==', _.productId).where('isNg', '==', true).where('status', '==', 'shelved')), [shippingRequest, version]);
  const inventoriesGroupedByProductId = groupBy(inventories, 'productId');

  return (
    <div>
      <div>
        <div>
          廃棄依頼数
        </div>
        {
          shippingRequest.items.map((item, i) => {
            return (
              <div key={i} className="d-flex">
                <div>
                  {tenant.id}-{item.productId}: {item.ngQuantity}
                </div>
              </div>
            );
          })
        }
        <div className="mt-2">
          廃棄済み合計: {(shippingRequest.inventoryIds || []).length}
        </div>
      </div>
      <table className="table table-bordered">
        <thead className="thead-light text-center">
          <tr>
            <th>
              バーコード
            </th>
            <th>
              棚
            </th>
            <th>
              販売不可在庫数
            </th>
            <th>
            </th>
          </tr>
        </thead>
        <tbody>
          {
            entries(inventoriesGroupedByProductId).map(([productId, inventories]) => {
              const inventoriesGroupedByLocationId = groupBy(inventories, 'locationId');
              return (
                <Fragment key={productId}>
                  {
                    entries(inventoriesGroupedByLocationId).map(([_, inventories], i) => {
                      const [{ locationId }] = inventories;
                      const location = locationsById[locationId];
                      const rowSpan = keys(inventoriesGroupedByLocationId).length;
                      const onClickDiscard = async () => {
                        const batch = db.batch();
                        const [inventory] = inventories;
                        batch.update(inventory.ref, {
                          shippingRequestId: shippingRequest.id,
                          pickedAt: new Date(),
                          status: 'picked'
                        });
                        batch.update(tenant.ref.collection('products').doc(productId), { ngInventoryIds: firebase.firestore.FieldValue.arrayRemove(inventory.id), });
                        batch.update(shippingRequest.ref, {
                          inventoryIds: firebase.firestore.FieldValue.arrayUnion(inventory.id),
                        });
                        await batch.commit();
                        updateVersion();
                      };

                      return (
                        <tr key={locationId}>
                          {
                            i === 0 && (
                              <th rowSpan={rowSpan}>
                                {idWithTenant(tenant.ref.collection('products').doc(productId))}-N
                              </th>
                            )
                          }
                          <td>
                            {locationId == null ? '(未棚入れ)' : (location && location.name)}
                          </td>
                          <td className="text-right">
                            {inventories.length}
                          </td>
                          <td>
                            <Button onClick={onClickDiscard}>
                              廃棄する
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  }
                </Fragment>
              );
            })
          }
        </tbody>
      </table>
    </div>
  );
}
